/*
 * One undo step per AI turn (notes/ai-assistant-phase1.md §3.5).
 *
 * Every write in an agent turn is recorded as normal history, tagged with the
 * turn id, and folded into the turn's existing entry so the whole turn undoes
 * with one Ctrl+Z. Pure (plain arrays in, plain arrays out) so it can be
 * unit tested without the Svelte stores.
 *
 * If the user edits something else mid-turn, their entry lands above the turn
 * entry. A later agent write can still fold into the turn entry below it, but
 * only when its patches cannot interact with the entries above (different
 * store, or a different event group). Undo then reverts the user's edit first
 * and the whole AI turn next, which is valid because those changes commute.
 * Otherwise the write starts a second entry for the same turn.
 */

/**
 * Coarse "what does this patch touch" key. eventGroupState patches are keyed
 * by group index; group-level structural patches (adding/removing a group,
 * replacing the whole array) touch every group.
 * @param {string} store
 * @param {{path: Array<string|number>}} patch
 */
function touchKey(store, patch) {
    if (store !== "eventGroupState") {
        return `${store}:*`;
    }
    const path = patch.path ?? [];
    return path.length < 2 ? `${store}:*` : `${store}:${path[0]}`;
}

/** @param {Record<string, any[]>} patchesByStore */
export function touchedKeys(patchesByStore) {
    const keys = new Set();
    for (const [store, patches] of Object.entries(patchesByStore ?? {})) {
        for (const patch of patches ?? []) {
            keys.add(touchKey(store, patch));
        }
    }
    return keys;
}

function keysOverlap(a, b) {
    for (const key of a) {
        const store = key.slice(0, key.indexOf(":"));
        if (b.has(key) || b.has(`${store}:*`)) {
            return true;
        }
        if (key.endsWith(":*") && [...b].some((other) => other.startsWith(`${store}:`))) {
            return true;
        }
    }
    return false;
}

/**
 * Whether two patch maps touch the same store/event group.
 * @param {Record<string, any[]>} a
 * @param {Record<string, any[]>} b
 */
export function patchesConflict(a, b) {
    return keysOverlap(touchedKeys(a), touchedKeys(b));
}

/**
 * Append forward patches and prepend inverse patches (same as coalescing).
 * @param {object} entry
 * @param {Record<string, any[]>} patches
 * @param {Record<string, any[]>} inversePatches
 */
export function mergeIntoEntry(entry, patches, inversePatches, time = Date.now()) {
    const merged = { ...entry, time, patches: { ...entry.patches }, inversePatches: { ...entry.inversePatches } };
    for (const [store, list] of Object.entries(patches)) {
        merged.patches[store] = [...(entry.patches?.[store] ?? []), ...list];
    }
    for (const [store, list] of Object.entries(inversePatches)) {
        merged.inversePatches[store] = [...list, ...(entry.inversePatches?.[store] ?? [])];
    }
    return merged;
}

/**
 * Fold a new history entry into its AI turn.
 *
 * `actions` must already have any redo tail removed (position === length).
 * Returns null when the entry cannot be folded and should be pushed as a new
 * entry instead.
 *
 * @param {{ actions: object[], savedPosition: number|null }} history
 * @param {{ turnId: string, patches: Record<string, any[]>, inversePatches: Record<string, any[]> }} entry
 * @returns {{ actions: object[], savedPosition: number|null } | null}
 */
export function foldIntoTurn(history, entry, time = Date.now()) {
    const { actions } = history;
    let target = -1;
    for (let i = actions.length - 1; i > 0; i--) {
        if (actions[i]?.turnId === entry.turnId) {
            target = i;
            break;
        }
    }
    if (target === -1) {
        return null;
    }

    for (let i = target + 1; i < actions.length; i++) {
        const above = actions[i];
        if (patchesConflict(above.patches, entry.patches) || patchesConflict(above.inversePatches, entry.patches)) {
            return null;
        }
    }

    const next = [...actions];
    next[target] = mergeIntoEntry(actions[target], entry.patches, entry.inversePatches, time);

    // A save at or after the turn entry no longer matches any position.
    let savedPosition = history.savedPosition;
    if (savedPosition != null && savedPosition > target) {
        savedPosition = null;
    }
    return { actions: next, savedPosition };
}

/**
 * Give every entry of a turn its final name.
 * @param {object[]} actions
 * @param {string} turnId
 * @param {string} name
 * @returns {{ actions: object[], count: number }}
 */
export function renameTurn(actions, turnId, name) {
    let count = 0;
    const next = actions.map((action) => {
        if (action?.turnId !== turnId) {
            return action;
        }
        count++;
        return { ...action, name };
    });
    return { actions: next, count };
}
